import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Button from './common/Button';
import { LoadingState } from './common/Spinner';

const AddBrandModal = ({ isOpen, onClose, onSuccess, categoryId, excludeBrandIds = [] }) => {
  const toast = useToast();
  const suggestionRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [customBrands, setCustomBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form Fields
  const [selectedCategory, setSelectedCategory] = useState('');
  const [brandName, setBrandName] = useState('');
  const [packageType, setPackageType] = useState('BTL');
  
  // Multiple Size state (standard sizes only)
  const standardSizes = ['90ml', '180ml', '375ml', '500ml', '750ml', '1000ml', '1ltr'];
  const [selectedSizes, setSelectedSizes] = useState(['180ml']);

  // Autocomplete Suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Fetch categories and custom brands on open
  useEffect(() => {
    if (isOpen) {
      setSelectedCategory(categoryId ? categoryId.toString() : '');
      fetchCategories();
      fetchCustomBrands();
      // Reset form
      setBrandName('');
      setSelectedSizes(['180ml']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, categoryId]);

  // Handle click outside suggestions to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/stock/categories');
      const categoryOrder = ['BRANDY', 'WHISKY', 'RUM', 'GIN', 'VODKA', 'LIQUEUR', 'RTD', 'TEQUILA', 'WINE', 'BEER', 'NON-ALCOHOL'];
      const allCategories = response.data.categories || [];
      const ordered = categoryOrder
        .map(name => allCategories.find(c => c.name === name))
        .filter(Boolean);
      setCategories(ordered);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchCustomBrands = async () => {
    setLoading(true);
    try {
      const response = await api.get('/stock/custom-brands');
      setCustomBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching custom brands:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get matching brand name suggestions based on category
  const filteredSuggestions = customBrands
    .filter(b => b.category_id === parseInt(selectedCategory, 10))
    .map(b => b.name)
    .filter((name, index, self) => self.indexOf(name) === index) // Unique names
    .filter(name => name.toLowerCase().includes(brandName.toLowerCase()));

  const toggleSizeSelection = (size) => {
    setSelectedSizes(prev =>
      prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    if (!brandName.trim()) {
      toast.error('Please enter a brand name');
      return;
    }
    if (selectedSizes.length === 0) {
      toast.error('Please select at least one size/ML variant');
      return;
    }
    if (!packageType) {
      toast.error('Please select a packaging type');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        categoryId: parseInt(selectedCategory, 10),
        name: brandName.trim(),
        sizes: selectedSizes,
        packageType
      };

      const response = await api.post('/stock/custom-brands', payload);
      
      if (onSuccess && response.data.variants) {
        onSuccess(response.data.variants);
      }
      
      toast.success(response.data.message || 'Custom brand variants created successfully!');
      onClose();
    } catch (error) {
      console.error('Error creating custom brand:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create custom brand';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-brand-modal-title"
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col slide-in border border-transparent dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
          <h2 id="add-brand-modal-title" className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-plus-circle text-primary dark:text-accent"></i> Create Custom Brand Variant
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all text-xl font-bold"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="absolute inset-0 bg-white/70 dark:bg-slate-800/70 z-10 flex items-center justify-center rounded-2xl">
              <LoadingState label="Loading custom brands..." />
            </div>
          )}

          {/* Category Dropdown */}
          <div className="space-y-1">
            <label htmlFor="modal-category" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Category
            </label>
            <select
              id="modal-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled
              className="w-full px-3.5 py-2.5 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400 outline-none text-sm cursor-not-allowed font-semibold"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Brand Name Input + Auto-suggest */}
          <div className="space-y-1 relative" ref={suggestionRef}>
            <label htmlFor="modal-brand-name" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Brand Name
            </label>
            <input
              id="modal-brand-name"
              type="text"
              placeholder="e.g. FARMERS BRANDY"
              value={brandName}
              onChange={(e) => {
                setBrandName(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              className="w-full px-3.5 py-2.5 rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none text-sm focus:border-primary dark:focus:border-accent transition-all font-semibold"
              autoComplete="off"
            />
            {/* Suggestions list */}
            {showSuggestions && brandName && filteredSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20">
                {filteredSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setBrandName(suggestion);
                      setShowSuggestions(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 font-semibold"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Packaging Type */}
          <div className="space-y-1">
            <label htmlFor="modal-package-type" className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Packaging Type
            </label>
            <select
              id="modal-package-type"
              value={packageType}
              onChange={(e) => setPackageType(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none text-sm focus:border-primary dark:focus:border-accent transition-all cursor-pointer font-semibold"
            >
              <option value="BTL">BTL (Bottle)</option>
              <option value="TPACK">TPACK (Tetra Pack)</option>
              <option value="CAN">CAN</option>
            </select>
          </div>

          {/* Sizes Checkboxes grid */}
          <div className="space-y-2">
            <span className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Sizes / ML Options (Select Multiple)
            </span>
            <div className="grid grid-cols-2 gap-2">
              {standardSizes.map((size) => (
                <label
                  key={size}
                  className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all cursor-pointer text-sm font-semibold select-none ${
                    selectedSizes.includes(size)
                      ? 'border-primary bg-primary/5 text-primary dark:border-accent dark:bg-accent/10 dark:text-accent'
                      : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSizes.includes(size)}
                    onChange={() => toggleSizeSelection(size)}
                    className="w-4 h-4 rounded text-primary focus:ring-primary dark:text-accent dark:focus:ring-accent accent-primary dark:accent-accent shrink-0"
                  />
                  <span className="truncate">{size}</span>
                </label>
              ))}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 shrink-0">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Variant(s)'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddBrandModal;
